<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
    <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-shield-account</v-icon>
        Add Trust
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Trust Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Trust Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trustName"
                label="Trust Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-shield-account"
                :rules="[v => !!v || 'Trust name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trustNumber"
                label="Trust Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                :rules="[v => !!v || 'Trust number is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="trustType"
                :items="trustTypes"
                label="Trust Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-briefcase"
                :rules="[v => !!v || 'Trust type is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registrationDate"
                label="Registration Date"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-calendar"
                type="date"
                :rules="[v => !!v || 'Registration date is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="cifNumber"
                label="CIF Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-identifier"
                readonly
              />
            </v-col>
            

          </v-row>

          <v-row>
            <!-- Trustee Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Trustee Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trusteeName"
                label="Trustee Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'Trustee name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trusteeIdNumber"
                label="Trustee ID Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                :rules="idValidationRules"
                @input="formatTrusteeIdInput"
                placeholder="901101 0000 00 0"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trusteeEmail"
                label="Trustee Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Must be a valid email'
                ]"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="trusteePhone"
                label="Trustee Phone"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatTrusteePhone"
                placeholder="074 551 1155"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Beneficiary Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Beneficiary Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="beneficiaryName"
                label="Primary Beneficiary Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account-heart"
                :rules="[v => !!v || 'Beneficiary name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="beneficiaryIdNumber"
                label="Beneficiary ID Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                :rules="idValidationRules"
                @input="formatBeneficiaryIdInput"
                placeholder="901101 0000 00 0"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="beneficiaryEmail"
                label="Beneficiary Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="beneficiaryPhone"
                label="Beneficiary Phone"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                @input="formatBeneficiaryPhone"
                placeholder="074 551 1155"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Address Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Address Information</h3>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="addressLine1"
                label="Address Line 1"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home"
                :rules="[v => !!v || 'Address is required']"
              />
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="addressLine2"
                label="Address Line 2"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home-outline"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="city"
                label="City"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-city"
                :rules="[v => !!v || 'City is required']"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="postalCode"
                label="Postal Code"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-mailbox"
                :rules="[v => !!v || 'Postal code is required']"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="province"
                label="Province"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-map-marker"
                :rules="[v => !!v || 'Province is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Banking Details -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Banking Details</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="bankName"
                label="Bank Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-bank"
                :rules="[v => !!v || 'Bank name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="bankAccountNumber"
                label="Bank Account Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-credit-card"
                :rules="[v => !!v || 'Account number is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Trust Assets -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Trust Assets</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="assetValue"
                label="Total Asset Value (R)"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-currency-usd"
                type="number"
                :rules="[v => !!v || 'Asset value is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-textarea
                v-model="assetDescription"
                label="Asset Description"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-text"
                rows="2"
                :rules="[v => !!v || 'Asset description is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Membership Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Membership Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="membership"
                :items="membershipOptions"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                :rules="[v => !!v || 'Membership type is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="monthlyFee"
                label="Monthly Fee (R)"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-currency-usd"
                type="number"
                :rules="[v => !!v || 'Monthly fee is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="notes"
                label="Additional Notes"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-note-text"
                rows="3"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
               color="#002060"
                dark
                rounded
                size="large"
                :disabled="!valid || loading"
                :loading="loading"
                @click="saveTrust"
                class="mr-4"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ loading ? 'Saving...' : 'Save Trust' }}
              </v-btn>
              
              <v-btn
                color="grey"
                dark
                rounded
                size="large"
                @click="cancel"
              >
                <v-icon left>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { CIFGenerator } from '../utils/cifGenerator';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';
import { formatIdInput, getIdValidationRules } from '../utils/idNumberFormatter';

export default {
  name: 'AddTrust',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,

      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      cifNumber: '',
      
      // Trust Information
      trustName: '',
      trustNumber: '',
      trustType: '',
      trustTypes: [
        'Family Trust',
        'Business Trust',
        'Charitable Trust',
        'Testamentary Trust',
        'Living Trust',
        'Other'
      ],
      registrationDate: '',
      
      // Trustee Information
      trusteeName: '',
      trusteeIdNumber: '',
      trusteeEmail: '',
      trusteePhone: '',
      
      // Beneficiary Information
      beneficiaryName: '',
      beneficiaryIdNumber: '',
      beneficiaryEmail: '',
      beneficiaryPhone: '',
      
      // Address Information
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      province: '',
      
      // Banking Details
      bankName: '',
      bankAccountNumber: '',
      
      // Trust Assets
      assetValue: '',
      assetDescription: '',
      
      // Membership Information
      membership: '',
              membershipOptions: [
          { title: 'Domestic', value: 'Domestic' },
          { title: 'Individual', value: 'Individual' },
          { title: 'Individual & Domestic', value: 'Individual & Domestic' }
        ],
      monthlyFee: '',
      
      // Additional Information
      notes: ''
    };
  },
  computed: {
    phoneValidationRules() {
      return getPhoneValidationRules();
    },
    idValidationRules() {
      return getIdValidationRules();
    }
  },
  methods: {

    formatTrusteePhone() {
      this.trusteePhone = formatPhoneInput(this.trusteePhone);
    },

    formatBeneficiaryPhone() {
      this.beneficiaryPhone = formatPhoneInput(this.beneficiaryPhone);
    },

    formatTrusteeIdInput() {
      this.trusteeIdNumber = formatIdInput(this.trusteeIdNumber);
    },

    formatBeneficiaryIdInput() {
      this.beneficiaryIdNumber = formatIdInput(this.beneficiaryIdNumber);
    },

    async saveTrust() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        
        try {
          // Generate CIF number automatically if not provided
          if (!this.cifNumber) {
            try {
              const cifNumber = await CIFGenerator.generateCIF('trust');
              this.cifNumber = cifNumber;
            } catch (error) {
              console.error('Error generating CIF, using fallback:', error);
              const cifNumber = CIFGenerator.generateTestCIF('trust');
              this.cifNumber = cifNumber;
            }
          }

          const trustData = {
            // CIF Information
            cifNumber: this.cifNumber,
            
            // Trust Information
            trustName: this.trustName,
            trustNumber: this.trustNumber,
            trustType: this.trustType,
            registrationDate: this.registrationDate,
            
            // Trustee Information
            trusteeName: this.trusteeName,
            trusteeIdNumber: this.trusteeIdNumber,
            trusteeEmail: this.trusteeEmail,
            trusteePhone: this.trusteePhone,
            
            // Beneficiary Information
            beneficiaryName: this.beneficiaryName,
            beneficiaryIdNumber: this.beneficiaryIdNumber,
            beneficiaryEmail: this.beneficiaryEmail,
            beneficiaryPhone: this.beneficiaryPhone,
            
            // Address Information
            address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}, ${this.province}`,
            
            // Banking Details
            bankName: this.bankName,
            bankAccountNumber: this.bankAccountNumber,
            
            // Trust Assets
            assetValue: parseFloat(this.assetValue),
            assetDescription: this.assetDescription,
            
            // Membership Information
            membership: this.membership,
            monthlyFee: parseFloat(this.monthlyFee),
            
            // Additional Information
            notes: this.notes,
            
            // Metadata
            clientType: 'trust',
            dateAdded: new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            }),
            createdAt: new Date().toISOString(),
            status: 'active'
          };

          // Save to Firestore
          const docRef = await addDoc(collection(db, 'clients'), trustData);
          console.log('Trust added with ID: ', docRef.id);
          
          // Log the activity
          await ActivityLogger.logActivity('Trust Added', {
            clientId: docRef.id,
            trustName: this.trustName,
            trusteeName: this.trusteeName,
            addedBy: this.$store.getters.user?.displayName || this.$store.getters.user?.email?.split('@')[0] || 'Unknown'
          });
          
          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `Trust "${this.trustName}" added successfully!`;
          this.showNotification = true;
          
          // Reset form
          this.$refs.form.reset();
          
          // Navigate back to clients list
          setTimeout(() => {
            this.$router.push({ path: '/clients', query: { type: 'trusts' } });
          }, 2000);
          
        } catch (error) {
          console.error('Error adding trust:', error);
          
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to add trust. Please try again.';
          this.showNotification = true;
        } finally {
          this.loading = false;
        }
      }
    },
    
    cancel() {
      this.$router.push({ path: '/clients', query: { type: 'trusts' } });
    }
  },
  
     async created() {
     console.log('AddTrust component created');
   },

   async mounted() {
     console.log('AddTrust component mounted');
     console.log('Current cifNumber:', this.cifNumber);
     
     // Generate CIF number automatically when component is mounted
     if (!this.cifNumber) {
       console.log('Generating CIF number...');
       
       // First, try to generate a test CIF immediately for instant feedback
       const testCifNumber = CIFGenerator.generateTestCIF('trust');
       this.cifNumber = testCifNumber;
       console.log('Test CIF number set immediately:', testCifNumber);
       
       // Then try to generate a proper unique CIF
       try {
         const cifNumber = await CIFGenerator.generateCIF('trust');
         this.cifNumber = cifNumber;
         console.log('Unique CIF number generated and set:', cifNumber);
         console.log('cifNumber after setting:', this.cifNumber);
       } catch (error) {
         console.error('Error generating unique CIF, keeping test CIF:', error);
         console.log('Keeping test CIF number:', this.cifNumber);
       }
     } else {
       console.log('CIF number already exists:', this.cifNumber);
     }
   }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 